import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import config from 'constants/apiConfig';
import { fileExtensions } from 'components/DisasterGPT/DisasterGPTConstants';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import type { FileProcessingEntry } from 'components/FileManagement/hooks/useFileProcessingStatus';

/**
 * A file sent with a user message; mirrors COMS `ChatAttachment`. Files
 * uploaded from the composer live in Org Files (`fileId`); `blobPath` is an
 * image attached before that, stored with the chat.
 */
export type ChatAttachment = {
  attachmentId: string;
  blobPath?: string;
  fileId?: string;
  mimeType: string;
  name: string;
};

/**
 * Whether PRATUS AI can search an uploaded file yet. Images are read directly,
 * so they skip this.
 */
export type ChatFileIndexState = 'indexing' | 'indexed' | 'unindexed';

/** A file picked in the composer, before and after it is uploaded. */
export type PendingChatAttachment = {
  id: string;
  name: string;
  /** Object URL for an image thumbnail; absent for other files. */
  previewUrl?: string;
  status: 'uploading' | 'ready' | 'error';
  attachment?: ChatAttachment;
  /** Indexing reported by the upload, before the processing poll has a say. */
  ragReady?: boolean;
};

export const CHAT_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;
/** Org Files takes ten files per upload. */
export const MAX_CHAT_ATTACHMENTS = 10;
export const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_CHAT_IMAGE_DIMENSION = 2048;

/** What the composer's file picker offers: every Org Files type plus images. */
export const CHAT_UPLOAD_ACCEPT = [
  ...fileExtensions.flatMap((ext) => [ext, ext.toUpperCase()]),
  ...CHAT_IMAGE_TYPES,
].join(',');

export function isChatImageFile(file: { type: string }): boolean {
  return (CHAT_IMAGE_TYPES as readonly string[]).includes(file.type);
}

export function isChatImageAttachment(attachment: {
  mimeType: string;
}): boolean {
  return isChatImageFile({ type: attachment.mimeType });
}

/** Files from a drop or paste, in order. */
export function filesFrom(items: DataTransfer | null | undefined): File[] {
  if (!items) return [];
  return Array.from(items.files ?? []);
}

/** Image files from a drop or paste, in order. */
export function imageFilesFrom(items: DataTransfer | null | undefined): File[] {
  return filesFrom(items).filter(isChatImageFile);
}

/**
 * Indexing state of an uploaded file. `processing` lists the workspace's files
 * that are not indexed yet; once a file is missing from it, it has been
 * indexed.
 */
export function chatFileIndexState(
  item: PendingChatAttachment,
  processing: Record<string, FileProcessingEntry> | undefined
): ChatFileIndexState | null {
  const fileId = item.attachment?.fileId;
  if (item.status !== 'ready' || !fileId || !item.attachment) return null;
  if (isChatImageAttachment(item.attachment)) return null;
  const entry = processing?.[fileId];
  if (entry) {
    return entry.status === 'failed' || entry.status === 'not_indexable'
      ? 'unindexed'
      : 'indexing';
  }
  if (processing) return 'indexed';
  return item.ragReady ? 'indexed' : 'indexing';
}

/**
 * Uploads one composer file to Org Files, indexed for PRATUS AI; images are
 * shrunk first.
 */
export async function uploadChatFile(params: {
  file: File;
  groupGuid: string;
}): Promise<{ attachment: ChatAttachment; ragReady: boolean }> {
  const { file } = params;
  const upload = isChatImageFile(file)
    ? new File([await downscaleChatImage(file)], file.name, { type: file.type })
    : file;
  const [stored] = await uploadFilesToBackend({
    files: [upload],
    workspaceId: params.groupGuid,
    scopeType: 'organization',
  });
  if (!stored?.id) throw new Error('The file could not be stored.');
  return {
    attachment: {
      attachmentId: stored.id,
      fileId: stored.id,
      mimeType: file.type || stored.mimetype || 'application/octet-stream',
      name: file.name,
    },
    ragReady: Boolean(stored.rag_ready),
  };
}

/** Target size that keeps the aspect ratio with the long edge at `max`. */
export function scaledDimensions(
  width: number,
  height: number,
  max: number = MAX_CHAT_IMAGE_DIMENSION
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= max) return { width, height };
  const ratio = max / longEdge;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Shrinks large photos before upload. GIFs keep their frames, so they are sent
 * as-is; anything else is redrawn when its long edge is over the limit.
 */
export async function downscaleChatImage(file: File): Promise<Blob> {
  if (file.type === 'image/gif') return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const target = scaledDimensions(bitmap.width, bitmap.height);
  if (target.width === bitmap.width && target.height === bitmap.height) {
    bitmap.close();
    return file;
  }
  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, target.width, target.height);
  bitmap.close();
  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob ?? file), type, 0.9)
  );
}

const attachmentsUrl = () => `${config.apiGateway.disasterchat}/attachments`;

/**
 * Object URL for a chat image, stored with the chat or in Org Files, fetched
 * with the user's session.
 */
export function useChatAttachmentUrl(
  attachment: ChatAttachment,
  groupGuid: string | undefined,
  incidentId: string | undefined
): string | undefined {
  const query = useQuery({
    queryKey: [
      'chat-attachment',
      attachment.blobPath ?? attachment.fileId ?? attachment.attachmentId,
    ],
    enabled: Boolean(groupGuid) && isChatImageAttachment(attachment),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await axios.get<Blob>(`${attachmentsUrl()}/content`, {
        responseType: 'blob',
        params: {
          group_guid: groupGuid,
          incident_id: incidentId,
          blobPath: attachment.blobPath,
          fileId: attachment.blobPath ? undefined : attachment.fileId,
          mimeType: attachment.mimeType,
        },
      });
      return response.data;
    },
  });
  const url = useMemo(
    () => (query.data ? URL.createObjectURL(query.data) : undefined),
    [query.data]
  );
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );
  return url;
}
